import React, { useState } from 'react';
import { Phone, Mail, Send, MessageCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import './Contact.css';

const FORMSPREE_URL = process.env.REACT_APP_FORMSPREE_URL;

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    sessionType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset sessionType if interest changes away from book-session
      ...(name === 'interest' && value !== 'book-session' ? { sessionType: '' } : {})
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Combine interest and session type for the email
    let fullInterest = formData.interest;
    if (formData.interest === 'book-session' && formData.sessionType) {
      fullInterest = `Book a Session - ${formData.sessionType}`;
    }

    try {
      const response = await axios.post(FORMSPREE_URL, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        interest: fullInterest,
        sessionType: formData.sessionType,
        message: formData.message,
        _subject: `New Booking Inquiry from ${formData.name}`
      }, {
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (response.status === 200) {
        toast({
          title: "Message Sent!",
          description: "Thank you for reaching out. I'll be in touch soon.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          interest: '',
          sessionType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again or contact us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="heading-hero">Contact Us</h1>
          <p className="body-large" style={{ maxWidth: '700px', margin: '1.5rem auto 0' }}>
            Begin your journey to healing, embodiment, and transformation
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info-box">
              <h2 className="heading-2" style={{ marginBottom: '1.5rem' }}>Get in Touch</h2>
              <p className="body-medium" style={{ marginBottom: '2rem', color: 'var(--text-secondary)' }}>
                I'm here to support you on your healing journey. Reach out to discuss your needs and book your session.
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="method-icon">
                    <Phone size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Phone / WhatsApp</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <a href="tel:0828983507" className="body-medium contact-link">082 898 3507</a>
                      <a 
                        href="https://wa.me/27828983507" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        style={{ 
                          display: 'inline-flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '32px',
                          height: '32px',
                          backgroundColor: '#25D366',
                          borderRadius: '50%',
                          color: 'white',
                          transition: 'transform 0.2s ease'
                        }}
                        title="Chat on WhatsApp"
                      >
                        <MessageCircle size={18} />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="method-icon">
                    <Mail size={24} />
                  </div>
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '0.5rem' }}>Email</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      <a href="mailto:info@tshidi-tsimile.co.za" className="body-medium contact-link">info@tshidi-tsimile.co.za</a>
                      <a href="mailto:admin@tshidi-tsimile.co.za" className="body-medium contact-link">admin@tshidi-tsimile.co.za</a>
                      <a href="mailto:events@tshidi-tsimile.co.za" className="body-medium contact-link">events@tshidi-tsimile.co.za</a>
                      <a href="mailto:bookings@tshidi-tsimile.co.za" className="body-medium contact-link">bookings@tshidi-tsimile.co.za</a>
                      <a href="mailto:shop@tshidi-tsimile.co.za" className="body-medium contact-link">shop@tshidi-tsimile.co.za</a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="availability-note">
                <p className="body-small">
                  Available for individual sessions, workshops, corporate programs, and collaborations.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-box">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="body-small">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="Your name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="body-small">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="body-small">Phone Number</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    placeholder="082 123 4567"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="interest" className="body-small">I'm Interested In *</label>
                  <select
                    id="interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    required
                    className="form-select"
                  >
                    <option value="">Select an option</option>
                    <option value="book-session">Book a Session</option>
                    <option value="workshop">Workshops & Ceremonies</option>
                    <option value="corporate">Corporate Wellness Program</option>
                    <option value="mentorship">Spiritual Psychology Mentorship</option>
                    <option value="relationship">Sacred Union & Relationship Work</option>
                    <option value="womb-healing">Womb Healing & Feminine Embodiment</option>
                    <option value="speaking">Talks & Community Programs</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {/* Conditional Session Type dropdown - shows when Individual Healing Session is selected */}
                {formData.interest === 'book-session' && (
                  <div className="form-group">
                    <label htmlFor="sessionType" className="body-small">Select Session Type *</label>
                    <select
                      id="sessionType"
                      name="sessionType"
                      value={formData.sessionType}
                      onChange={handleChange}
                      required
                      className="form-select"
                    >
                      <option value="">Select a session type</option>
                      <option value="Couples/Marriage Counselling - R2500 (2 hours)">Couples/Marriage Counselling - R2500 (2 hours)</option>
                      <option value="Family Counselling (4 people max) - R2500 (2 hours)">Family Counselling (4 people max) - R2500 (2 hours)</option>
                      <option value="Individual Counselling - R1700 (1 hour 30 min)">Individual Counselling - R1700 (1 hour 30 min)</option>
                      <option value="Spiritual Guidance/Counselling - R1700 (1 hour 30 min)">Spiritual Guidance/Counselling - R1700 (1 hour 30 min)</option>
                      <option value="Sex Counselling/Coaching - R1700 (1 hour 30 min)">Sex Counselling/Coaching - R1700 (1 hour 30 min)</option>
                      <option value="Couples Sex Counselling - R2500 (2 hours)">Couples Sex Counselling - R2500 (2 hours)</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="message" className="body-small">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="form-textarea"
                    placeholder="Tell me about your intentions and what you're seeking..."
                  />
                </div>

                <button type="submit" className="btn-primary" disabled={isSubmitting} style={{ width: '100%' }}>
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send size={18} style={{ marginRight: '0.5rem' }} />
                      Send Message
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="contact-extra-section">
        <div className="container">
          <div className="contact-extra-content">
            <h2 className="heading-2" style={{ marginBottom: '1rem' }}>What to Expect</h2>
            <p className="body-medium" style={{ marginBottom: '1.5rem' }}>
              When you reach out, we'll have an initial conversation to understand your needs and intentions. From there, we'll create a personalized plan that honours your unique journey.
            </p>
            <p className="body-medium">
              All sessions and workshops are held in a safe, sacred space where you can explore, heal, and transform at your own pace.
            </p>
          </div>
        </div>
      </section>

      {/* Terms & Policies */}
      <section className="policy-section">
        <div className="container">
          <div className="policy-content">
            <h2 className="heading-1" style={{ marginBottom: '2rem', textAlign: 'center' }}>Terms & Policies</h2>
            
            <div className="policy-item">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>1. Payment & Refund Policy</h3>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                All courses must be paid in full before access is granted, unless a prior written arrangement has been made with The Tshidi Tsimile Company.
              </p>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                Due to the digital nature of all courses, programs, and materials, all sales are final and non-refundable.
              </p>
              <p className="body-medium">
                The Client understands that payment secures access to the selected program and that access is non-transferable under any circumstances.
              </p>
            </div>

            <div className="policy-item">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>2. Intellectual Property & Usage Rights</h3>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                All course materials, concepts, processes, and recordings remain the exclusive intellectual property of The Tshidi Tsimile Company.
              </p>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                The Client may not copy, share, distribute, publish, or reproduce any content without prior written consent.
              </p>
              <p className="body-medium">
                Any unauthorized use, distribution, or reproduction of content may result in legal action.
              </p>
            </div>

            <div className="policy-item">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>3. Liability Disclaimer</h3>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                The Tshidi Tsimile Company does not guarantee any specific outcomes, results, or transformations from participation in its programs.
              </p>
              <p className="body-medium">
                The Client acknowledges that they are solely responsible for their decisions, actions, and results during and after the program.
              </p>
            </div>

            <div className="policy-item">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>4. Confidentiality & Community Conduct</h3>
              <p className="body-medium" style={{ marginBottom: '0.75rem' }}>
                The Client agrees to maintain confidentiality of all discussions, shared experiences, and proprietary content within the program or community space.
              </p>
              <p className="body-medium">
                The Tshidi Tsimile Company reserves the right to remove any Client from the program—without refund—for inappropriate conduct, including but not limited to harassment, disruptive behavior, or sharing sensitive or proprietary content outside of the designated container.
              </p>
            </div>

            <div className="policy-item">
              <h3 className="heading-3" style={{ marginBottom: '1rem' }}>5. Agreement Acceptance</h3>
              <p className="body-medium">
                By purchasing, enrolling in, or accessing any program, the Client acknowledges that they have read, understood, and agreed to the full terms and conditions of this Agreement.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};